import { useState, useEffect } from "react";
import api, { downloadBlob, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Printer, ShieldCheck, Check, Minus } from "lucide-react";

const ACTION_LETTER = { view: "V", create: "C", edit: "E", delete: "D", manage: "M" };

function Cell({ actions, c }) {
  return (
    <div className="flex flex-wrap gap-x-2 gap-y-0.5" data-testid="perm-cell">
      {actions.map((a) => {
        const ok = c[a];
        return (
          <span key={a} className={`inline-flex items-center gap-0.5 text-xs font-medium ${ok ? "text-[#1F7A33]" : "text-slate-300"}`} title={a}>
            <span className="tabular-nums">{ACTION_LETTER[a]}</span>
            {ok ? <Check className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
          </span>
        );
      })}
    </div>
  );
}

export default function PermissionMatrix() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/permissions/matrix").then((r) => setData(r.data)).catch((e) => toast.error(formatErr(e.response?.data?.detail))); }, []);

  const exportPdf = async () => {
    setBusy(true);
    try { await downloadBlob("/permissions/matrix.pdf", "ChitChat-Permission-Matrix.pdf"); }
    catch { toast.error("Could not generate PDF"); }
    finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm text-slate-400" data-testid="perm-loading">Loading permission matrix…</p>;

  let lastGroup = null;
  return (
    <div data-testid="admin-permissions">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-2 text-xs text-slate-500">
          <ShieldCheck className="h-4 w-4 text-[#0094A8] shrink-0 mt-0.5" />
          <span>Read-only reference of what each role can do. This mirrors the rules enforced on the server — it does not change any permissions.
            <span className="block mt-1">Legend: <b>V</b> View · <b>C</b> Create · <b>E</b> Edit · <b>D</b> Delete · <b>M</b> Manage &nbsp;|&nbsp; <Check className="inline h-3 w-3 text-[#1F7A33]" /> Allowed · <Minus className="inline h-3 w-3 text-slate-300" /> Not allowed</span>
          </span>
        </div>
        <Button variant="outline" onClick={exportPdf} disabled={busy} data-testid="perm-export-pdf"><Printer className="h-4 w-4" />Print / Export PDF</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[920px]">
          <thead>
            <tr className="border-b bg-[#0F2A3A] text-left text-xs font-semibold uppercase tracking-wide text-white">
              <th className="px-4 py-3 w-56">Permission area</th>
              {data.roles.map((r) => <th key={r.key} className="px-4 py-3">{r.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.areas.map((a) => {
              const header = a.group !== lastGroup ? a.group : null;
              lastGroup = a.group;
              return (
                <>
                  {header && (
                    <tr key={`g-${header}`} className="bg-slate-50"><td colSpan={data.roles.length + 1} className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#0094A8]">{header}</td></tr>
                  )}
                  <tr key={a.area} className="border-b border-slate-100 align-top" data-testid={`perm-row-${a.area}`}>
                    <td className="px-4 py-2.5">
                      <p className="font-semibold text-slate-800">{a.area}</p>
                      {a.note && <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{a.note}</p>}
                    </td>
                    {data.roles.map((r) => (
                      <td key={r.key} className="px-4 py-2.5"><Cell actions={a.actions} c={a.cells[r.key]} /></td>
                    ))}
                  </tr>
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
